/**
 * THE ORDINARY BUTTON. The one that looks like every other button on the page.
 *
 * This is the variant a customer picks when they do NOT want a widget: it is this
 * project's own `Button` primitive with a microphone in it, and the whole design
 * goal is that it inherits the surrounding theme rather than announcing itself. So
 * unlike `flag-button.tsx` — which states every colour inline because it renders in
 * a shadow root with no stylesheet — this file uses the ui/ primitives and Tailwind
 * classes on purpose.
 *
 * THE DEPENDENCY THAT COMES WITH THAT, stated once so nobody rediscovers it the
 * hard way: `Button` resolves `bg-primary`, `text-destructive` and the radius scale
 * from custom properties, and `:root` — where `web/src/index.css` declares them —
 * DOES NOT CROSS A SHADOW BOUNDARY. So this component only looks like itself where
 * a stylesheet declaring those tokens has been adopted into the root it renders in.
 * In the panel that is `index.css`; in the embed it is `src/embed/embed.css`, which
 * declares the same tokens on `:host` and lists `components/voice` as a Tailwind
 * `@source` precisely so the classes below exist. Break either of those and this
 * button degrades to transparent backgrounds and inherited text — whereas
 * `flag-button.tsx` holds its look with no stylesheet at all, which is why the flag
 * is the variant that renders correctly no matter what.
 *
 * `accent`/`onAccent` are the escape hatch either way: both become inline styles.
 *
 * FOUR STATES, AND ONE OF THEM IS DELIBERATELY NOT A STATE.
 *
 * Idle, connecting, live, and error — where error RENDERS AS IDLE with a sentence
 * beside it. A failed call leaves the user needing exactly one thing, which is the
 * button they just pressed, and a widget that replaces itself with an error box
 * makes them hunt for the way back. Not a toast either: a toast outlives the cause
 * and needs a toaster mounted somewhere, which an embedded button cannot assume.
 *
 * THE MUTE TOGGLE IS A SECOND CONTROL, NOT A THIRD STATE OF THE FIRST. Muting and
 * hanging up are the two things a person wants mid-call and conflating them into
 * one button that changes meaning is how people hang up when they meant to mute.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { MicIcon, MicOffIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { isLive, type VoiceControl, type VoicePhase, type VoicePlacement, type VoiceWidgetCommon } from './types'
import { withTakeover } from './stage-text'

export interface PlainButtonProps extends VoiceWidgetCommon {
  control: VoiceControl
  size?: 'sm' | 'default' | 'lg'
  /** When set, the button and its controls are wrapped in a fixed-position
   *  container at that placement. 'inline' (default) renders in flow. */
  placement?: VoicePlacement
  zIndex?: number
  showTimer?: boolean
}

/** Same reasoning as the flag's: high enough to win on a stranger's page, with
 *  headroom left above it. See the note in flag-button.tsx. */
const DEFAULT_Z = 2147483000

export function PlainButton({
  control,
  size = 'default',
  placement = 'inline',
  zIndex = DEFAULT_Z,
  showTimer = true,
  label = 'Talk to us',
  endLabel = 'End call',
  accent,
  onAccent,
  caption,
}: PlainButtonProps) {
  const phase = control.phase
  const live = isLive(phase)
  const busy = phase === 'connecting'
  const failed = phase === 'error'
  const [hover, setHover] = useState(false)

  /**
   * `accent` overrides the theme, but ONLY while the button starts a call.
   *
   * While live the button is `destructive`, and destructive is not a decoration —
   * it is the reason a person can find the hang-up control without reading it. A
   * brand colour on the end-call button would take that away, so the accent is
   * dropped for the duration and returns when the call does.
   */
  const accented = !live && !busy && (accent != null || onAccent != null)
  const accentStyle: CSSProperties | undefined = accented
    ? {
        ...(accent == null ? {} : { backgroundColor: accent, borderColor: accent }),
        ...(onAccent == null ? {} : { color: onAccent }),
        // An inline background beats `hover:bg-primary/80`, so the hover feedback
        // has to be re-created by hand or it is simply gone.
        ...(hover ? { filter: 'brightness(0.93)' } : {}),
      }
    : undefined

  const iconSize = size === 'sm' ? 'icon-sm' : size === 'lg' ? 'icon-lg' : 'icon'

  const row = (
    <div className="inline-flex items-center gap-2">
      {live ? (
        <>
          <Button
            type="button"
            size={size}
            variant="destructive"
            onClick={control.stop}
            aria-label={endLabel}
            onPointerEnter={() => setHover(true)}
            onPointerLeave={() => setHover(false)}
          >
            {/* ONE GLYPH, NOT TWO. This used to be an outlined square followed by a
                20 px `OrbCanvas`, on the argument that the orb was the only thing
                proving the call was still alive. Two marks in front of two words is a
                crowded button, and the second one was a canvas with its own render
                loop, its own `role="img"` to hide from the screen reader, and a theme
                argument to keep it off the page's limited WebGL contexts. The stop
                square now does that job itself: filled, so it reads as the live,
                pressable half of a two-control row, and haloed off the same `levels()`
                the orb was reading. */}
            <StopPulse levels={control.levels} />
            {endLabel}
            {showTimer ? <Timer fromMs={control.startedAtMs} /> : null}
          </Button>
          <Button
            type="button"
            size={iconSize}
            variant="outline"
            onClick={control.toggleMute}
            aria-pressed={control.muted}
            aria-label={control.muted ? 'Unmute microphone' : 'Mute microphone'}
            title={control.muted ? 'Unmute microphone' : 'Mute microphone'}
          >
            {control.muted ? <MicOffIcon aria-hidden /> : <MicIcon aria-hidden />}
          </Button>
        </>
      ) : (
        <Button
          type="button"
          size={size}
          onClick={control.start}
          // Idempotent in the session, but a pressable button mid-connect is what
          // produces the second press in the first place.
          disabled={busy}
          aria-label={busy ? 'Connecting, please wait' : failed ? `${label} — retry` : label}
          style={accentStyle}
          onPointerEnter={() => setHover(true)}
          onPointerLeave={() => setHover(false)}
        >
          {busy ? (
            // A dot rather than a spinning glyph: `animate-pulse` is core Tailwind
            // and ships in both stylesheets, but if it ever did not, a still dot
            // beside "Connecting…" still reads as "wait" — a frozen spinner reads
            // as a hung page.
            <span
              className="size-2 shrink-0 animate-pulse rounded-full bg-current"
              aria-hidden
            />
          ) : (
            <MicIcon aria-hidden />
          )}
          {busy ? 'Connecting…' : label}
        </Button>
      )}
    </div>
  )

  const body = (
    // CENTRED UNDER THE BUTTON, not ragged down its left edge. The column holds a
    // caption and a failure sentence, and both are ABOUT the control above them —
    // `items-start` left them hanging off one end of a row whose width changes with
    // the phase (the live row is a wide pill plus a mute button; the idle row is one
    // button), so the caption appeared to move when nothing had moved. Same rule the
    // orb widget already follows.
    <div className="inline-flex flex-col items-center gap-1 text-center">
      {row}
      {/* Beside the button, in the flow, and only while it is true. Sized in px,
          not rem: this renders inside the embed's shadow root, where rem tracks
          the HOST page's root font-size. 352px ≡ the old 22rem. */}
      {failed && control.error ? (
        <p className="max-w-[352px] text-sm text-destructive" role="alert">
          {control.error}
        </p>
      ) : null}
      {caption ? <p className="text-[11.2px] font-light text-muted-foreground">{caption}</p> : null}
      <span aria-live="polite" className="sr-only">
        {withTakeover(control, statusSentence(phase, control.error))}
      </span>
    </div>
  )

  if (placement === 'inline') return body
  return (
    <div className={cn('fixed', PLACEMENT[placement])} style={{ zIndex }}>
      {body}
    </div>
  )
}

/** The six floating positions. `inline` is handled before this map is reached. */
const PLACEMENT: Record<Exclude<VoicePlacement, 'inline'>, string> = {
  left: 'left-4 top-1/2 -translate-y-1/2',
  right: 'right-4 top-1/2 -translate-y-1/2',
  'bottom-left': 'bottom-4 left-4',
  'bottom-right': 'bottom-4 right-4',
  'top-left': 'top-4 left-4',
  'top-right': 'top-4 right-4',
}

/**
 * mm:ss, ticked by a one-second interval off `startedAtMs`.
 *
 * Its own component with its own interval, exactly like `Elapsed` in
 * components/call/call-status.tsx: kept in the parent it would re-render the orb's
 * host element and the mute button once a second for a number neither of them
 * shows. `startedAtMs` is a fixed epoch value, so what ticks is the clock, not a
 * prop being pushed in from above.
 */
function Timer({ fromMs }: { fromMs: number | null }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (fromMs == null) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [fromMs])
  if (fromMs == null) return null
  const total = Math.max(0, Math.floor((now - fromMs) / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return (
    <span className="tabular-nums opacity-70">
      {String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}
    </span>
  )
}

/**
 * THE FILLED STOP SQUARE, WITH A HALO THAT FOLLOWS THE CALL.
 *
 * `currentColor` throughout, so the square is whatever red the `destructive` variant
 * resolved to and it follows the theme without being told. The halo is a second
 * square behind the first, blurred and scaled — an OPACITY and a TRANSFORM are the
 * two things a browser animates without touching layout or paint, which is what makes
 * a 60 Hz loop on somebody else's page acceptable. Alpha of the token would have been
 * the obvious way to write the glow and there is no way to compute one here:
 * `currentColor` is not a value this file can read.
 *
 * `levels()` is called INSIDE the frame, never held as a prop — the interface says so
 * for a reason. Two behaviours it forces:
 *
 *   - INSTANT ATTACK, SLOW RELEASE, the same asymmetry as `lib/call/meter.ts`. A glow
 *     that decays as fast as it rises flickers on every syllable; one that rises
 *     slowly is late for the word it is reacting to.
 *   - A BREATH UNDERNEATH IT. `levels()` may legitimately return zeroes forever —
 *     the fallback recorder path has no AudioContext — and a dead-still glyph on a
 *     live call is the exact thing this element exists to disprove. The sine floor
 *     keeps it alive on `phase` alone, which is what the orb's phase-driven states
 *     used to do here.
 *
 * Reduced motion gets the resting halo and no loop at all.
 */
const RELEASE_MS = 260
const BREATH_MS = 1400
const BREATH_DEPTH = 0.34

function StopPulse({ levels }: { levels: VoiceControl['levels'] }) {
  const halo = useRef<HTMLSpanElement | null>(null)
  useEffect(() => {
    const el = halo.current
    if (el === null) return
    const still =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (still) {
      el.style.opacity = '0.3'
      return
    }
    let frame = 0
    let last = 0
    let glow = 0
    const draw = (now: number) => {
      // Re-armed before the work, so a throw in here cannot silently stop the loop.
      frame = requestAnimationFrame(draw)
      const dt = last === 0 ? 16 : Math.min(64, now - last)
      last = now
      const { user, assistant } = levels()
      // 1.7 because conversational speech sits low in a 0..1 meter; the clamp is what
      // stops a cough from blowing the halo out to twice the button's height.
      const target = Math.min(1, Math.max(user, assistant) * 1.7)
      glow = target >= glow ? target : Math.max(target, glow - dt / RELEASE_MS)
      const breath = (0.5 - 0.5 * Math.cos((now / BREATH_MS) * Math.PI * 2)) * BREATH_DEPTH
      const lit = Math.max(glow, breath)
      el.style.opacity = (0.16 + lit * 0.46).toFixed(3)
      el.style.transform = `scale(${(1 + lit * 0.85).toFixed(3)})`
    }
    frame = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(frame)
  }, [levels])
  return (
    <span
      aria-hidden
      className="relative inline-flex shrink-0 items-center justify-center"
      style={{ width: 16, height: 16 }}
    >
      <span
        ref={halo}
        style={{
          position: 'absolute',
          width: 12,
          height: 12,
          borderRadius: 3,
          background: 'currentColor',
          filter: 'blur(3px)',
          opacity: 0.16,
          willChange: 'opacity, transform',
        }}
      />
      <span
        style={{
          position: 'relative',
          width: 12,
          height: 12,
          borderRadius: 3,
          background: 'currentColor',
        }}
      />
    </span>
  )
}

/** Coarse transitions only. The three live phases collapse into one sentence for
 *  the reason spelled out in flag-button.tsx: a polite live region that follows
 *  listening/thinking/speaking talks over the agent. */
function statusSentence(phase: VoicePhase, error: string | null): string {
  switch (phase) {
    case 'idle':
      return ''
    case 'connecting':
      return 'Connecting.'
    case 'listening':
    case 'thinking':
    case 'speaking':
      return 'Connected. The call is running.'
    case 'ended':
      return 'Call ended.'
    case 'error':
      return error ?? 'The call could not be connected.'
  }
}
